import { useState } from 'react';
import { T, useGT, Var } from 'gt-react';
import { PlusIcon, SparkleIcon } from '@phosphor-icons/react';
import type { FeedbackSupplier, RenewalRule } from '@erp/rpc';

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import { RuleDialog } from '../RuleDialog';
import { useRuleEditing } from '../ruleEditing';
import { usePricingPermissions } from '../usePricingPermissions';
import { RenewalRuleEditor } from './RenewalRuleEditor';
import { RenewalRuleRow, UnreadableRenewalRules } from './RenewalRuleRow';
import {
  EMPTY_RENEWAL_RULE_FORM,
  formFromRule,
  type RenewalRuleDraft,
  type RenewalRuleFormState,
} from './renewalRuleForm';
import {
  renewalRuleKindNames,
  sensitiveListName,
  standardRenewalRules,
} from './renewalRuleText';
import { renewalRulesPanelState, saveAll } from './renewalRulesPanelState';
import { useRenewalRuleMutations, useRenewalRules } from './useRenewalRules';

interface Props {
  /** Whose rules to list and add. */
  supplier: FeedbackSupplier;
}

function PanelMessage({ isError }: { isError: boolean }) {
  if (isError) {
    return (
      <p className="text-destructive text-sm">
        <T context="Mass Renewal rules panel error when the rules cannot be loaded">
          The Renewal Rules could not be loaded. Reload the page to try again.
        </T>
      </p>
    );
  }
  return (
    <p className="text-muted-foreground text-sm">
      <T context="empty state of the Mass Renewal rules panel">
        No rules yet: every line gets the Feedback File’s increases.
      </T>
    </p>
  );
}

/** Asks before a rule is deleted; `rule` null keeps it closed. */
function DeleteRenewalRuleDialog({
  rule,
  onConfirm,
  onClose,
}: {
  rule: RenewalRule | null;
  onConfirm: (rule: RenewalRule) => void;
  onClose: () => void;
}) {
  const gt = useGT();
  return (
    <AlertDialog
      open={rule !== null}
      onOpenChange={(open) => !open && onClose()}
    >
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>
            <T context="title of the confirmation before deleting a Mass Renewal rule">
              Delete <Var>{rule?.name}</Var>?
            </T>
          </AlertDialogTitle>
          <AlertDialogDescription>
            <T context="body of the confirmation before deleting a Mass Renewal rule">
              The next Mass Renewal will not apply this rule. This cannot be
              undone.
            </T>
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel>
            {gt('Cancel', { context: 'dialog cancel' })}
          </AlertDialogCancel>
          <AlertDialogAction
            onClick={() => {
              if (rule) onConfirm(rule);
              onClose();
            }}
          >
            {gt('Delete', {
              context: 'confirm deleting a Mass Renewal rule',
            })}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

/** Offers the pricing team's standard rules to a supplier with none. */
function AddStandardRulesButton({
  isPending,
  onClick,
}: {
  isPending: boolean;
  onClick: () => void;
}) {
  return (
    <div>
      <Button
        size="sm"
        variant="secondary"
        disabled={isPending}
        onClick={onClick}
      >
        <SparkleIcon className="h-4 w-4" />
        <T context="button that creates the pricing team's standard Mass Renewal rules">
          Add Standard Rules
        </T>
      </Button>
    </div>
  );
}

/**
 * One supplier's Renewal Rules: the standing exceptions applied to the
 * Feedback File's increases on each Mass Renewal. Each change is saved
 * straight away; adding or editing a rule opens a dialog.
 */
export function RenewalRulesPanel({ supplier }: Props) {
  const gt = useGT();
  const { canCreate, canUpdate, canDelete } = usePricingPermissions();
  const { rules, unreadableRuleIds, isLoading, isError } =
    useRenewalRules(supplier);
  const { create, update, remove } = useRenewalRuleMutations();
  const [deleting, setDeleting] = useState<RenewalRule | null>(null);
  const { editing, open, close, save, rowActions } = useRuleEditing<
    RenewalRule,
    RenewalRuleFormState
  >({
    canUpdate,
    canDelete,
    formFromRule,
    toggle: ({ id }, enabled) => update.mutate({ id, enabled }),
    remove: setDeleting,
  });
  const { showMessage, offerStandardRules } = renewalRulesPanelState({
    ruleCount: rules.length,
    unreadableCount: unreadableRuleIds.length,
    isLoading,
    isError,
    canCreate,
  });

  function openNew(form: Partial<RenewalRuleFormState> = {}): void {
    open(null, {
      ...EMPTY_RENEWAL_RULE_FORM,
      name: renewalRuleKindNames(gt)[EMPTY_RENEWAL_RULE_FORM.type],
      ...form,
    });
  }

  function saveRule(draft: RenewalRuleDraft): void {
    save(draft, {
      update: (id, changes, onSuccess) =>
        update.mutate({ id, ...changes }, { onSuccess }),
      create: (rule, onSuccess) =>
        create.mutate({ ...rule, supplier, enabled: true }, { onSuccess }),
    });
  }

  /**
   * The pricing team's two list-free rules are saved at once; the sensitive
   * list needs its items, so its editor opens next.
   */
  async function addStandardRules(): Promise<void> {
    const saved = await saveAll(standardRenewalRules(gt), (draft) =>
      create.mutateAsync({ ...draft, supplier, enabled: true }),
    );
    if (saved)
      openNew({ type: 'holdListedItems', name: sensitiveListName(gt) });
  }

  return (
    <section className="flex min-w-0 flex-col gap-2">
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <h3 className="text-sm font-medium">
            <T context="heading of the Mass Renewal rules panel">
              Renewal Rules
            </T>
          </h3>
          <p className="text-muted-foreground text-xs">
            <T context="explanation under the Mass Renewal rules heading">
              Change a line’s margin and core cost increases before it is
              priced. Holds run first, then floors.
            </T>
          </p>
        </div>
        {canCreate ? (
          <Button size="sm" variant="outline" onClick={() => openNew()}>
            <PlusIcon className="h-4 w-4" />
            <T context="button that adds a Mass Renewal rule">Add Rule</T>
          </Button>
        ) : null}
      </div>

      <ul>
        {rules.map((rule) => (
          <RenewalRuleRow
            key={rule.id}
            rule={rule}
            isBusy={update.isPending || remove.isPending}
            {...rowActions(rule)}
          />
        ))}
      </ul>
      {showMessage ? <PanelMessage isError={isError} /> : null}
      {offerStandardRules ? (
        <AddStandardRulesButton
          isPending={create.isPending}
          onClick={() => void addStandardRules()}
        />
      ) : null}
      <UnreadableRenewalRules
        ids={unreadableRuleIds}
        isBusy={remove.isPending}
        onDelete={canDelete ? (id) => remove.mutate({ id }) : null}
      />

      <RuleDialog
        isOpen={editing.mode === 'open'}
        onClose={close}
        className="max-w-2xl"
        title={
          <T context="title of the Mass Renewal rule dialog">Renewal Rule</T>
        }
      >
        {editing.mode === 'open' ? (
          <RenewalRuleEditor
            key={editing.ruleId ?? 'new'}
            initial={editing.form}
            isSaving={create.isPending || update.isPending}
            onCancel={close}
            onSave={saveRule}
          />
        ) : null}
      </RuleDialog>

      <DeleteRenewalRuleDialog
        rule={deleting}
        onConfirm={({ id }) => remove.mutate({ id })}
        onClose={() => setDeleting(null)}
      />
    </section>
  );
}
